<template>
  <div class="sales-reports-charts lov-card">
    <h6 class="lov-card-title mb-3">{{ title }}</h6>
    <div class="chart-wrap">
      <div
        v-if="loading"
        class="chart-placeholder"
      >
        <b-spinner small />
        <span class="ml-2 text-muted">Đang tải...</span>
      </div>
      <div
        v-else-if="!hasData"
        class="chart-placeholder text-muted"
      >
        Không có dữ liệu để hiển thị biểu đồ
      </div>
      <v-chart
        v-else
        :option="chartOption"
        class="chart-canvas"
        autoresize
      />
    </div>
  </div>
</template>

<script>
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import moment from 'moment';

use([BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

export default {
  name: 'SalesReportsCharts',
  components: { VChart },
  props: {
    chartType: { type: String, default: 'bar-horizontal' },
    title: { type: String, default: '' },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    limit: { type: Number, default: 8 },
    primaryColor: { type: String, default: '#3B82F6' },
    secondaryColor: { type: String, default: '#22C55E' },
    storeNameA: { type: String, default: 'CH A' },
    storeNameB: { type: String, default: 'CH B' },
  },
  computed: {
    hasData() {
      return this.chartRows.length > 0;
    },
    chartRows() {
      return [...this.rows];
    },
    chartOption() {
      switch (this.chartType) {
        case 'line':
          return this.buildLineOption();
        case 'donut':
          return this.buildDonutOption();
        case 'bar-dual':
          return this.buildDualBarOption();
        case 'bar-grouped':
          return this.buildGroupedBarOption();
        case 'bar-horizontal':
        default:
          return this.buildHorizontalBarOption();
      }
    },
  },
  methods: {
    labelKey(row) {
      return (
        row.tenHang ||
        row.productName ||
        row.spc ||
        row.tenSpc ||
        row.sanPhamCha ||
        row.nhaCungCap ||
        row.tenKho ||
        row.maKho ||
        row.loaiHoaDon ||
        row.nhomKhach ||
        row.cayDanhMuc ||
        '—'
      );
    },
    valueKey(row) {
      return Number(
        row.doanhThu ||
          row.tongDoanhSo ||
          row.thanhTienSauVat ||
          row.tongGiaTriTonKho ||
          row.giaBan ||
          0,
      );
    },
    profitKey(row) {
      return Number(row.laiGop || row.tongLai || 0);
    },
    buildLineOption() {
      const grouped = {};
      this.chartRows.forEach((row) => {
        const day = row.ngayHoaDon || row.reportDate;
        if (!day) {
          return;
        }
        const key = moment(day).isValid() ? moment(day).format('MM-DD') : String(day);
        grouped[key] = (grouped[key] || 0) + Number(row.doanhThu || 0);
      });
      const dates = Object.keys(grouped).sort();
      return {
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '8%', top: '8%', containLabel: true },
        xAxis: { type: 'category', data: dates, boundaryGap: false },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: (v) => this.formatAxis(v) },
        },
        series: [
          {
            name: 'Doanh thu',
            type: 'line',
            smooth: true,
            data: dates.map((d) => grouped[d]),
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(59,130,246,0.35)' },
                  { offset: 1, color: 'rgba(59,130,246,0.02)' },
                ],
              },
            },
            itemStyle: { color: this.primaryColor },
            lineStyle: { width: 2 },
          },
        ],
      };
    },
    buildDonutOption() {
      const data = this.chartRows
        .filter((row) => Math.abs(this.valueKey(row)) > 0)
        .map((row) => ({
          name: this.labelKey(row),
          value: Math.abs(this.valueKey(row)),
        }));
      return {
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, type: 'scroll' },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '45%'],
            data,
            label: { show: false },
          },
        ],
      };
    },
    buildDualBarOption() {
      const sorted = [...this.chartRows]
        .sort((a, b) => this.valueKey(b) - this.valueKey(a))
        .slice(0, this.limit);
      const names = sorted.map((row) => this.labelKey(row));
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { bottom: 0, data: ['Doanh số', 'Lãi'] },
        grid: { left: '2%', right: '4%', bottom: '12%', top: '4%', containLabel: true },
        xAxis: {
          type: 'value',
          axisLabel: { formatter: (v) => this.formatAxis(v) },
        },
        yAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            formatter: (v) => (v.length > 24 ? `${v.substring(0, 24)}…` : v),
          },
        },
        series: [
          {
            name: 'Doanh số',
            type: 'bar',
            data: sorted.map((row) => this.valueKey(row)),
            itemStyle: { color: this.primaryColor, borderRadius: [0, 4, 4, 0] },
          },
          {
            name: 'Lãi',
            type: 'bar',
            data: sorted.map((row) => this.profitKey(row)),
            itemStyle: { color: this.secondaryColor, borderRadius: [0, 4, 4, 0] },
          },
        ],
      };
    },
    buildGroupedBarOption() {
      const categories = this.chartRows.map((row) => row.cayDanhMuc || '—');
      const storeA = this.chartRows.map((row) => Number(row.cuaHangA?.doanhThu || 0));
      const storeB = this.chartRows.map((row) => Number(row.cuaHangB?.doanhThu || 0));
      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: [this.storeNameA, this.storeNameB] },
        grid: { left: '3%', right: '4%', bottom: '14%', top: '8%', containLabel: true },
        xAxis: { type: 'category', data: categories },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: (v) => this.formatAxis(v) },
        },
        series: [
          {
            name: this.storeNameA,
            type: 'bar',
            data: storeA,
            itemStyle: { color: this.primaryColor, borderRadius: [4, 4, 0, 0] },
          },
          {
            name: this.storeNameB,
            type: 'bar',
            data: storeB,
            itemStyle: { color: this.secondaryColor, borderRadius: [4, 4, 0, 0] },
          },
        ],
      };
    },
    buildHorizontalBarOption() {
      const sorted = [...this.chartRows]
        .sort((a, b) => this.valueKey(b) - this.valueKey(a))
        .slice(0, this.limit);
      const names = sorted.map((row) => this.labelKey(row));
      const values = sorted.map((row) => this.valueKey(row));
      const color = this.chartType === 'bar-horizontal' && this.primaryColor === '#3B82F6'
        ? this.primaryColor
        : this.primaryColor;
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '2%', right: '4%', bottom: '4%', top: '4%', containLabel: true },
        xAxis: {
          type: 'value',
          axisLabel: { formatter: (v) => this.formatAxis(v) },
        },
        yAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            formatter: (v) => (v.length > 22 ? `${v.substring(0, 22)}…` : v),
          },
        },
        series: [
          {
            type: 'bar',
            data: values,
            itemStyle: { color, borderRadius: [0, 4, 4, 0] },
          },
        ],
      };
    },
    formatAxis(value) {
      const num = Number(value);
      if (num >= 1e9) {
        return `${(num / 1e9).toFixed(2)}B`;
      }
      if (num >= 1e6) {
        return `${(num / 1e6).toFixed(1)}M`;
      }
      if (num >= 1e3) {
        return `${(num / 1e3).toFixed(0)}K`;
      }
      return String(num);
    },
  },
};
</script>

<style scoped>
.sales-reports-charts .chart-wrap {
  min-height: 280px;
}
.chart-canvas {
  height: 280px;
  width: 100%;
}
.chart-placeholder {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lov-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.lov-card-title {
  font-weight: 600;
  color: #111827;
  margin: 0;
}
</style>
